import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Filter, Image as ImageIcon, Images, Play, Search, Sparkles, Video, X } from "lucide-react";
import { api, assetUrl } from "../../lib/api";

const fallbackMedia = [
  { id: "f1", title: "Campus Life", caption: "Students and institutional activities across campus.", category: "Campus", media_type: "image", media_url: "/images/IMG-20250909-WA0012.jpg" },
  { id: "f2", title: "Academic Environment", caption: "Learning spaces, departments and campus facilities.", category: "Academic", media_type: "image", media_url: "/images/IMG-20250909-WA0013.jpg" },
  { id: "f3", title: "Campus Events", caption: "Official activities, ceremonies and student life.", category: "Events", media_type: "image", media_url: "/images/IMG-20250909-WA0015.jpg" },
  { id: "f4", title: "Institutional Moments", caption: "Public moments from the Federal Polytechnic Bida community.", category: "Institution", media_type: "image", media_url: "/images/IMG-20250909-WA0016.jpg" },
];

function isVideo(item) {
  return item.media_type === "video" || String(item.media_url || "").match(/\.(mp4|webm|mov)$/i);
}

function isVirtualTour(item) {
  return item.media_type === "virtual_tour" || String(item.media_url || "").includes("youtube") || String(item.media_url || "").includes("vimeo");
}

function normalize(item) {
  return {
    ...item,
    title: item.title || "Gallery media",
    caption: item.caption || item.album_title || "Official gallery media.",
    category: item.category || item.album_title || "Campus",
    media_url: assetUrl(item.media_url),
  };
}

export default function MediaGallery() {
  const [media, setMedia] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [album, setAlbum] = useState("all");
  const [type, setType] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([api("/api/gallery/media"), api("/api/gallery/albums")])
      .then(([mediaRows, albumRows]) => {
        if (!mounted) return;
        const liveMedia = Array.isArray(mediaRows) ? mediaRows.filter((item) => item.media_url).map(normalize) : [];
        setMedia(liveMedia.length ? liveMedia : fallbackMedia.map(normalize));
        setAlbums(Array.isArray(albumRows) ? albumRows : []);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message);
        setMedia(fallbackMedia.map(normalize));
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const categories = useMemo(() => {
    return ["all", ...Array.from(new Set(media.map((item) => item.category).filter(Boolean)))];
  }, [media]);

  const filtered = useMemo(() => {
    return media.filter((item) => {
      const albumOk = album === "all" || String(item.album_id || "") === album;
      const typeOk = type === "all" || item.media_type === type || (type === "image" && !isVideo(item) && !isVirtualTour(item));
      const haystack = `${item.title} ${item.caption} ${item.category} ${item.album_title || ""}`.toLowerCase();
      return albumOk && typeOk && haystack.includes(query.toLowerCase());
    });
  }, [media, album, type, query]);

  const stats = [
    ["Total Media", media.length, Images],
    ["Images", media.filter((item) => !isVideo(item) && !isVirtualTour(item)).length, ImageIcon],
    ["Videos", media.filter(isVideo).length, Video],
    ["Albums", albums.length || "CMS", Camera],
  ];

  return (
    <main className="min-h-screen bg-slate-50 text-slate-950">
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(37,99,235,.32),transparent_34%),radial-gradient(circle_at_80%_10%,rgba(16,185,129,.24),transparent_32%),linear-gradient(135deg,#07111f,#102a5c_45%,#0f766e)]" />
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px),linear-gradient(90deg,rgba(255,255,255,.7) 1px,transparent 1px)", backgroundSize: "44px 44px" }} />
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24">
          <p className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-blue-100 backdrop-blur">
            <Sparkles className="h-4 w-4 text-emerald-300" /> Official Media Centre
          </p>
          <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_420px] lg:items-end">
            <div>
              <h1 className="max-w-4xl text-5xl font-black leading-[0.95] tracking-tight md:text-7xl">Campus Gallery</h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-white/80">Explore official campus photos, academic events, facility highlights, videos and media uploaded from the admin gallery CMS.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {stats.map(([label, value, Icon], index) => (
                <motion.div key={label} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.06 }} className="rounded-2xl border border-white/10 bg-white/10 p-5 shadow-xl backdrop-blur">
                  <Icon className="mb-3 h-5 w-5 text-emerald-300" />
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-white/60">{label}</p>
                  <div className="mt-2 text-3xl font-black">{value}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      <section className="sticky top-0 z-20 border-y border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4 sm:px-6 lg:flex-row lg:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-100">
            <Search className="h-5 w-5 shrink-0 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search gallery media..." className="min-w-0 flex-1 bg-transparent text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400" />
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-3 text-xs font-black uppercase tracking-wider text-slate-600">
              <Filter className="h-4 w-4" />
              <select value={album} onChange={(e) => setAlbum(e.target.value)} className="bg-transparent outline-none">
                <option value="all">All Albums</option>
                {albums.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
              </select>
            </label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-600 outline-none">
              <option value="all">All Media</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="virtual_tour">Virtual Tours</option>
            </select>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        {error && <div className="mb-6 rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm font-bold text-yellow-900">Live gallery could not load fully: {error}. Showing fallback media where necessary.</div>}
        <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-blue-700">Browse Media</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight md:text-4xl">Official Gallery Collection</h2>
          </div>
          <p className="text-sm font-black text-slate-500">{loading ? "Loading..." : `${filtered.length} item(s) available`}</p>
        </div>

        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-3xl bg-slate-200" />)}</div>
        ) : (
          <div className="grid auto-rows-[260px] gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((item, index) => (
              <motion.button
                key={item.id || `${item.media_url}-${index}`}
                onClick={() => setSelected(item)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.12 }}
                transition={{ duration: 0.45, delay: index * 0.03 }}
                whileHover={{ y: -6 }}
                className={`group relative overflow-hidden rounded-[1.6rem] bg-slate-200 text-left shadow-lg shadow-slate-200/70 ring-1 ring-slate-200 ${index === 0 ? "sm:col-span-2 sm:row-span-2" : ""}`}
              >
                {isVirtualTour(item) ? (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-950 to-blue-900 text-white"><Play className="mr-2 h-7 w-7" /> Virtual Tour</div>
                ) : isVideo(item) ? (
                  <video src={assetUrl(item.media_url)} className="h-full w-full object-cover" muted />
                ) : (
                  <img src={assetUrl(item.media_url)} alt={item.title} onError={(e) => { e.currentTarget.src = "/image-fallback.svg"; }} className="h-full w-full object-cover transition duration-700 group-hover:scale-110" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/88 via-slate-950/20 to-transparent opacity-0 transition duration-500 group-hover:opacity-100" />
                <div className="absolute inset-x-0 bottom-0 translate-y-3 p-5 text-white opacity-0 transition duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="mb-2 inline-flex rounded-full bg-white/20 px-3 py-1 text-[11px] font-black uppercase tracking-wider backdrop-blur">{item.category || item.media_type}</p>
                  <h2 className="text-lg font-black leading-tight">{item.title}</h2>
                  <p className="mt-1 line-clamp-2 text-sm text-white/80">{item.caption}</p>
                </div>
              </motion.button>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && <div className="rounded-3xl bg-white p-10 text-center text-slate-500 shadow-sm ring-1 ring-slate-200">No gallery media available yet. Publish media from the admin Gallery Media CMS.</div>}
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelected(null)}>
            <motion.div initial={{ scale: 0.96, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 16 }} className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[2rem] bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b p-4 md:p-5">
                <div>
                  <h2 className="text-xl font-black text-slate-900">{selected.title}</h2>
                  <p className="text-sm text-slate-500">{selected.caption}</p>
                </div>
                <button onClick={() => setSelected(null)} className="rounded-full bg-slate-100 p-3 font-black text-slate-700"><X className="h-5 w-5" /></button>
              </div>
              <div className="bg-black">
                {isVirtualTour(selected) ? (
                  <iframe title={selected.title} src={assetUrl(selected.media_url)} className="h-[72vh] w-full" allowFullScreen />
                ) : isVideo(selected) ? (
                  <video src={assetUrl(selected.media_url)} controls className="max-h-[72vh] w-full" />
                ) : (
                  <img src={assetUrl(selected.media_url)} alt={selected.title} onError={(e) => { e.currentTarget.src = "/image-fallback.svg"; }} className="mx-auto max-h-[72vh] object-contain" />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
